import { useState } from "react";
import { Link } from "react-router-dom";
import { PublicNav, PublicFooter } from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { CATEGORIES } from "@/lib/jobFields";
import { Loader2, Radar, CheckCircle2, Clock, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";

const inputCls = "bg-[#F4F4F5] border-black/10 rounded-none";
const SIZES = ["Independent / solo", "2–10", "11–50", "51–200", "200+"];
const VOLUMES = ["1–2 / month", "3–5 / month", "6–10 / month", "10+ / month"];

export default function ForRecruiters() {
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({
    full_name: "", email: "", role_title: "", agency: "", website: "", linkedin_url: "",
    agency_size: "", focus_areas: [], roles: "", industries: "", locations: "",
    monthly_volume: "", wants_candidate_access: true, message: "",
  });
  const setV = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleFocus = (v) => setF({ ...f, focus_areas: f.focus_areas.includes(v) ? f.focus_areas.filter((x) => x !== v) : [...f.focus_areas, v] });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/recruiter-waitlist", f);
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicNav />
      <section className="max-w-6xl mx-auto px-6 pt-32 pb-16 grid lg:grid-cols-2 gap-16 items-start">
        <div className="lg:sticky lg:top-28">
          <div className="inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.2em] text-primary border border-primary/30 px-3 py-1">
            <Clock className="h-3 w-3" /> Coming soon
          </div>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl mt-6 leading-[0.95]">
            A board for<br /><span className="text-primary">recruiting partners.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-lg">
            We're opening the Innovation Talent Partners platform to select agencies and independent
            recruiters. We're deepening our candidate community first, so every posting lands with the right people.
          </p>

          <div className="mt-8 border border-black/10 bg-white p-5 space-y-4">
            <p className="overline text-[0.55rem]">Join the waitlist and get</p>
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
              <div><p className="text-sm font-heading font-bold">Candidate portal access</p><p className="text-xs text-muted-foreground">Search and shortlist our vetted candidate directory when partner access opens.</p></div>
            </div>
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
              <div><p className="text-sm font-heading font-bold">Founding-partner features & rates</p><p className="text-xs text-muted-foreground">Early access, analytics and priority support at launch pricing.</p></div>
            </div>
            <div className="flex items-start gap-3">
              <Radar className="h-5 w-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
              <div><p className="text-sm font-heading font-bold">Or post a one-off role</p><p className="text-xs text-muted-foreground">Prefer a single listing? Standard postings will be available from <b className="text-[#09090B]">$200</b> — no membership required.</p></div>
            </div>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">Hiring directly? <Link to="/for-clients" className="text-primary hover:underline">Post a role as a client</Link></p>
        </div>

        <div className="border border-black/10 bg-white">
          {done ? (
            <div className="p-10 text-center" data-testid="waitlist-success">
              <div className="h-12 w-12 mx-auto bg-primary/10 flex items-center justify-center"><CheckCircle2 className="h-6 w-6 text-primary" /></div>
              <h2 className="font-heading font-bold text-xl mt-6">You're on the list</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
                Thanks{f.full_name ? `, ${f.full_name.split(" ")[0]}` : ""} — we'll reach out to <b className="text-[#09090B]">{f.email}</b> as soon as recruiting-partner access opens.
              </p>
              <Link to="/" className="mt-8 inline-block text-sm text-primary hover:underline">Back to home</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="p-8 space-y-5" data-testid="recruiter-waitlist-form">
              <p className="text-sm text-muted-foreground">Tell us a bit about your practice so we can tailor your launch access. No payment, no commitment.</p>

              <p className="overline text-[0.55rem]">About you</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Your name</Label><Input data-testid="wl-name" value={f.full_name} onChange={setV("full_name")} required className={inputCls} /></div>
                <div className="space-y-2"><Label>Work email</Label><Input type="email" data-testid="wl-email" value={f.email} onChange={setV("email")} required className={inputCls} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Your title</Label><Input data-testid="wl-title" value={f.role_title} onChange={setV("role_title")} placeholder="e.g. Managing Partner" className={inputCls} /></div>
                <div className="space-y-2"><Label>LinkedIn</Label><Input data-testid="wl-linkedin" value={f.linkedin_url} onChange={setV("linkedin_url")} placeholder="linkedin.com/in/…" className={inputCls} /></div>
              </div>

              <p className="overline text-[0.55rem] pt-2">Your practice</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Agency / firm</Label><Input data-testid="wl-agency" value={f.agency} onChange={setV("agency")} placeholder="Independent or firm name" className={inputCls} /></div>
                <div className="space-y-2"><Label>Website</Label><Input data-testid="wl-website" value={f.website} onChange={setV("website")} placeholder="https://" className={inputCls} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Team size</Label>
                  <Select value={f.agency_size} onValueChange={(v) => setF({ ...f, agency_size: v })}>
                    <SelectTrigger data-testid="wl-size" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-white border-black/10">{SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Roles / month</Label>
                  <Select value={f.monthly_volume} onValueChange={(v) => setF({ ...f, monthly_volume: v })}>
                    <SelectTrigger data-testid="wl-volume" className={inputCls}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-white border-black/10">{VOLUMES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Focus areas <span className="text-muted-foreground font-normal">(select all that apply)</span></Label>
                <div className="grid grid-cols-3 gap-px bg-black/10 border border-black/10">
                  {CATEGORIES.map((c) => {
                    const on = f.focus_areas.includes(c);
                    return (
                      <button type="button" key={c} data-testid={`wl-focus-${c}`} onClick={() => toggleFocus(c)}
                        className={`p-2.5 text-xs capitalize text-left transition-colors ${on ? "bg-[#09090B] text-white" : "bg-white hover:bg-black/5"}`}>{c}</button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-2"><Label>Roles you typically hire for</Label><Input data-testid="wl-roles" value={f.roles} onChange={setV("roles")} placeholder="e.g. VP Eng, GTM leaders, board seats" className={inputCls} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Industries</Label><Input data-testid="wl-industries" value={f.industries} onChange={setV("industries")} placeholder="fintech, ai, climate" className={inputCls} /></div>
                <div className="space-y-2"><Label>Locations</Label><Input data-testid="wl-locations" value={f.locations} onChange={setV("locations")} placeholder="Remote, SF, NYC" className={inputCls} /></div>
              </div>

              <label className="flex items-start gap-3 border border-black/10 bg-[#F4F4F5] p-3 cursor-pointer" data-testid="wl-candidate-access">
                <input type="checkbox" checked={f.wants_candidate_access} onChange={(e) => setF({ ...f, wants_candidate_access: e.target.checked })} className="mt-0.5 accent-[#002EA6]" />
                <span className="text-sm">I'm interested in <b>candidate portal access</b> to search & shortlist vetted talent.</span>
              </label>
              <div className="space-y-2"><Label>Anything else? <span className="text-muted-foreground font-normal">(optional)</span></Label><Textarea data-testid="wl-message" value={f.message} onChange={setV("message")} className={`${inputCls} min-h-[70px]`} /></div>

              <Button type="submit" disabled={saving} data-testid="wl-submit" className="w-full h-11 rounded-none bg-primary hover:bg-[#09090B] transition-colors">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join the waitlist"}
              </Button>
            </form>
          )}
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
